/**
 * Central catalog of analytics events.
 *
 * Every custom (non-pageview) event the site tracks is declared here as a named method, so this
 * file is the single source of truth for "what are we measuring." Pageviews are automatic; these
 * are the in-page interactions (AJAX, clicks) that change no URL and would otherwise be invisible.
 *
 * Events fire to whichever cookieless tool is loaded — Umami (`umami.track(name, data)`) and/or
 * Plausible (`plausible(name, { props })`). In dev/localhost neither script is present, so every
 * call is a silent no-op. Errors are swallowed: analytics must never break the page.
 *
 * Plausible note: each event name below must also be registered as a Custom Event goal in the
 * Plausible dashboard (Site Settings → Goals) before it appears there. Umami lists events
 * automatically with no setup.
 *
 * To add an event: add a method here (don't call the tool globals directly from components), then
 * invoke `analytics.<name>(…)` from the component.
 */

interface UmamiGlobal {
  track?: (name: string, data?: Record<string, unknown>) => void;
}
type PlausibleGlobal = (name: string, options?: { props?: Record<string, unknown> }) => void;

declare global {
  interface Window {
    umami?: UmamiGlobal;
    plausible?: PlausibleGlobal;
  }
}

/** Low-level dispatch to every present tool. Kept private so the catalog below stays the only API. */
function track(name: string, props: Record<string, unknown> = {}): void {
  try {
    window.umami?.track?.(name, props);
    window.plausible?.(name, { props });
  } catch {
    /* analytics must never interfere with the page */
  }
}

export const analytics = {
  /** Home job feed (FeaturedJobs): visitor clicked "load more" and a page loaded successfully. */
  loadMore(page: number, jobsShown: number): void {
    track('Load More', { page, jobsShown });
  },

  /**
   * Alert-signup modal opened. `mode` separates the soft "get these by email" prompt from the hard
   * verify-wall — a `wall` value also marks that the visitor hit the anonymous depth cap.
   */
  signupPromptShown(mode: 'soft' | 'wall'): void {
    track('Signup Prompt Shown', { mode });
  },

  /** Alert signup accepted server-side (opt-in email sent). The real conversion. */
  signupCompleted(mode: 'soft' | 'wall'): void {
    track('Signup Completed', { mode });
  },

  /**
   * Alert signup rejected at submit. `reason` is `domain_blocked` (disposable inbox / no mail
   * server — our server-side rejection) or `invalid_format` (bean-validation bounce). Lets us watch
   * how often the disposable-email block fires in production.
   */
  signupRejected(reason: 'domain_blocked' | 'invalid_format'): void {
    track('Signup Rejected', { reason });
  },
};
